import { ApiClient } from "@webng/tickaroo-api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@webng/react-app-common";
import { useQuery } from "@tanstack/react-query";
import { Reaction, ReactionsMessage } from "@webng-types/changebridge";

interface WebSocketMessage {
    type: 'connected' | 'error' | 'reactions' | 'pong' | 'subscribeResponse';
    message?: string;
    eventId?: string;
    reactionId?: string;
    count?: number;
    status?: string;
    data?: {
        counts: {
            [eventId: string]: {
                [reactionId: string]: number;
            };
        };
        timestamp: number;
    };
}

export function useReactions(liveblogId: string, eventIds: string[], reactionsEnabled: boolean) {
    const [reactionCounts, setReactionCounts] = useState<{[eventId: string]: {[reactionId: string]: number}} | undefined>(undefined);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [loadedEvents, setLoadedEvents] = useState<Set<string>>(new Set());
    const wsRef = useRef<WebSocket | null>(null);
    const api = useApi();
    const heartbeatIntervalRef = useRef<number>();
    const heartbeatTimeoutRef = useRef<number>();
    const reconnectTimeoutRef = useRef<number>();
    const retryCountRef = useRef(0);
    const pendingSubscriptionsRef = useRef<Set<string>>(new Set());

    const createHeartbeatTimeoutFn = useCallback((ws: WebSocket) => {
        return () => {
            ws.close();
        };
    }, []);

    const connect = useCallback(() => {
        try {
            const url = new URL(api._apiUrl(ApiClient.API_REACTIONS_V1_WS, {liveblogId: liveblogId}));
            url.protocol = "wss:";
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                setIsConnected(true);
                setError(null);
                retryCountRef.current = 0;

                // Start heartbeat
                heartbeatIntervalRef.current = setInterval(() => {
                    ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
                }, 20000);

                // Set initial heartbeat timeout
                const heartbeatTimeoutFn = createHeartbeatTimeoutFn(ws);
                heartbeatTimeoutRef.current = setTimeout(heartbeatTimeoutFn, 60000);

                // Send any pending subscriptions
                if (pendingSubscriptionsRef.current.size > 0) {
                    const eventIds = Array.from(pendingSubscriptionsRef.current);
                    ws.send(JSON.stringify({
                        type: 'subscribe',
                        eventIds
                    }));
                    pendingSubscriptionsRef.current.clear();
                }
            };

            ws.onmessage = (event) => {
                const data: WebSocketMessage = JSON.parse(event.data);
                
                if (data.type === "pong") {
                    // Reset heartbeat timeout
                    if (heartbeatTimeoutRef.current) {
                        clearTimeout(heartbeatTimeoutRef.current);
                    }
                    const heartbeatTimeoutFn = createHeartbeatTimeoutFn(ws);
                    heartbeatTimeoutRef.current = setTimeout(heartbeatTimeoutFn, 60000);
                    return;
                }

                if (data.type === 'subscribeResponse' && data.status === 'success' && data.data?.counts) {
                    setReactionCounts(prev => ({
                        ...prev,
                        ...data.data!.counts
                    }));
                    return;
                }

                if(data.type === 'reactions') {
                    const reactionsData = data as ReactionsMessage;

                    setReactionCounts(prev => {
                        const counts: { [eventId: string]: { [reactionId: string]: number } } = { ...prev }
                        reactionsData.reactions.forEach(r => {
                            counts[r.eventId] = {
                                ...counts[r.eventId],
                                [r.reactionId]: r.count
                            }
                        })
                        return counts;
                    })
                    return;
                }
            };

            ws.onerror = () => {
                setError(new Error('WebSocket connection error'));
                setIsConnected(false);
            };

            ws.onclose = () => {
                setIsConnected(false);
                clearInterval(heartbeatIntervalRef.current);
                clearTimeout(heartbeatTimeoutRef.current);

                // Attempt to reconnect
                if (retryCountRef.current < 5) {
                    retryCountRef.current += 1;
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect();
                    }, Math.min(1000 * Math.pow(2, retryCountRef.current), 30000));
                }
            };

        } catch (error) {
            setError(error as Error);
            setIsConnected(false);
        }
    }, [api, liveblogId, createHeartbeatTimeoutFn]);

    const onVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible' && wsRef.current?.readyState === WebSocket.CLOSED) {
            retryCountRef.current = 0;
            connect();
        }
    }, [connect]);

    const onNetworkChange = useCallback(() => {
        if (navigator.onLine && wsRef.current?.readyState === WebSocket.CLOSED) {
            retryCountRef.current = 0;
            connect();
        }
    }, [connect]);

    const subscribe = useCallback((eventIds: string[]) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            eventIds.forEach(id => pendingSubscriptionsRef.current.add(id));
            return;
        }

        const message = {
            type: 'subscribe',
            eventIds
        };
        try {
            wsRef.current.send(JSON.stringify(message));
        } catch (error) {
            // Handle error silently or log to error reporting service
        }
    }, []);

    const unsubscribe = useCallback((eventIds: string[]) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            eventIds.forEach(id => pendingSubscriptionsRef.current.delete(id));
            return;
        }

        const message = {
            type: 'unsubscribe',
            eventIds
        };
        try {
            wsRef.current.send(JSON.stringify(message));
        } catch (error) {
            // Handle error silently or log to error reporting service
        }
    }, []);

    useEffect(() => {
        if(!reactionsEnabled) {
            return;
        }

        if(eventIds.every(id => loadedEvents.has(id))) {
            return;
        }

        const addedEvents = eventIds.filter(id => !loadedEvents.has(id));
        const removedEvents = Array.from(loadedEvents).filter(id => !eventIds.includes(id));

        if(addedEvents.length > 0) {
            subscribe(addedEvents);
        }

        if(removedEvents.length > 0) {
            unsubscribe(removedEvents);
        }

        return setLoadedEvents(new Set(eventIds));

    }, [loadedEvents, subscribe, unsubscribe, eventIds, reactionsEnabled])

    useEffect(() => {
        if(!reactionsEnabled) {
            return;
        }

        document.addEventListener("visibilitychange", onVisibilityChange, false);
        window.addEventListener("online", onNetworkChange, false);
        window.addEventListener("offline", onNetworkChange, false);

        connect();

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange, false);
            window.removeEventListener("online", onNetworkChange, false);
            window.removeEventListener("offline", onNetworkChange, false);
            clearInterval(heartbeatIntervalRef.current);
            clearTimeout(heartbeatTimeoutRef.current);
            clearTimeout(reconnectTimeoutRef.current);
            if (wsRef.current) {
                wsRef.current.close(1000, "Component unmounted");
            }
        };
    }, [connect, onVisibilityChange, onNetworkChange, reactionsEnabled]);

    return {
        reactionCounts,
        isConnected,
        error
    };
}

export function useUserReactions(liveblogId: string | undefined, userId: string | undefined, organizationId: string | undefined) {
    const api = useApi();
    return useQuery({
        queryKey: ["userReactions", liveblogId, userId, organizationId],
        queryFn: async () => {
            if (!liveblogId || !userId || !organizationId) return undefined;
            // Assumes the API returns all user reactions for all events in the liveblog
            // If the API is different, adjust accordingly
            const response: {reactions: Reaction[]} = await api.getJson(ApiClient.API_REACTIONS_V1_GET, {
                organizationId: organizationId,
                liveblogId,
                userId,
            });
            const reactions = response.reactions.filter(reaction => reaction.liveblogId === liveblogId);
            
            // Convert array of reactions into a map of {eventId: {reactionId: true}}
            const eventReactionsMap = reactions.reduce((acc, reaction) => {
                const eventId = reaction.reactionKey.split("#")[1];
                const reactionId = reaction.reactionId;
                
                if (!acc[eventId]) {
                    acc[eventId] = {};
                }
                acc[eventId][reactionId] = true;
                
                return acc;
            }, {} as {[eventId: string]: {[reactionId: string]: boolean}});
            
            return eventReactionsMap;
        },
        enabled: !!liveblogId && !!userId && !!organizationId,
    });
} 